import { useEffect, useMemo, useState } from 'react';

export const PAGE_SIZES = [10, 15, 25, 50];

export function getPaginationItems(totalPages, currentPage) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
  const pages = new Set([1, totalPages, currentPage]);
  if (currentPage > 1) pages.add(currentPage - 1);
  if (currentPage < totalPages) pages.add(currentPage + 1);
  const sorted = [...pages].sort((a, b) => a - b);
  const items = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) items.push(`ellipsis-${p}`);
    items.push(p);
  });
  return items;
}

// items: array of raw records
// searchFields(item) => array of strings to match against the search query
// statusOf(item) => string used for status-tab filtering + counts
// sorters: { [colKey]: item => comparable value } (falls back to item[colKey])
export function useTableControls({
  items,
  searchFields,
  statusOf,
  sorters,
  defaultSort,
  defaultDir = 'desc',
  pageSize: initialPageSize = 10,
}) {
  const [search, setSearch] = useState('');
  const [activeStatus, setActiveStatus] = useState('all');
  const [sortCol, setSortCol] = useState(defaultSort);
  const [sortDir, setSortDir] = useState(defaultDir);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [currentPage, setCurrentPage] = useState(1);

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      if (activeStatus !== 'all' && statusOf && statusOf(item) !== activeStatus) return false;
      if (q && searchFields) {
        const hay = searchFields(item).filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, search, activeStatus, searchFields, statusOf]);

  const sorted = useMemo(() => {
    if (!sortCol) return filtered;
    const getVal = sorters && sorters[sortCol] ? sorters[sortCol] : (item) => item[sortCol];
    return [...filtered].sort((a, b) => {
      const av = getVal(a) ?? '';
      const bv = getVal(b) ?? '';
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortCol, sortDir, sorters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, activeStatus, pageSize]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const pageStart = sorted.length === 0 ? 0 : (currentPage - 1) * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, sorted.length);
  const paginated = sorted.slice(pageStart, pageEnd);

  const counts = useMemo(() => {
    if (!statusOf) return {};
    return items.reduce((acc, item) => {
      const s = statusOf(item);
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {});
  }, [items, statusOf]);

  return {
    search,
    setSearch,
    activeStatus,
    setActiveStatus,
    sortCol,
    sortDir,
    handleSort,
    pageSize,
    setPageSize,
    currentPage,
    setCurrentPage,
    filtered,
    sorted,
    paginated,
    totalPages,
    pageStart,
    pageEnd,
    counts,
    paginationItems: getPaginationItems(totalPages, currentPage),
  };
}
